/**
 * The barcode scanner, read as a keyboard.
 *
 * Almost every scanner sold into this market presents as a USB HID keyboard and
 * types the barcode followed by Enter. That needs no native code at all, which
 * is the whole point: the same heuristic works in a browser and in the shell.
 *
 * The Electron app went straight to `node-hid`, kept a vendor and product id in
 * `electron-store`, and then never opened the device — `initializeHardware`
 * sends `{ connected: true, message: 'Ready' }` on the strength of those two
 * ids being present. The wedge was what was actually working.
 */
export interface WedgeOptions {
  /** Characters arriving faster than this are not a person typing. */
  readonly maxGapMs?: number
  /** Shorter than this and it is somebody leaning on the keyboard. */
  readonly minLength?: number
  /** Give up on a partial scan after this, so a stray keystroke does not stick. */
  readonly resetMs?: number
}

/**
 * Watch for scans.
 *
 * Ignores anything typed into a focused input: the settings screen has fields,
 * and a scanner firing into one of them while it also fires a scan event is one
 * barcode entered twice.
 */
export function watchWedge(
  onScan: (code: string) => void,
  options: WedgeOptions = {},
  target: Pick<Document, 'addEventListener' | 'removeEventListener'> & { activeElement?: Element | null } = document
): () => void {
  const maxGap = options.maxGapMs ?? 35
  const minLength = options.minLength ?? 4
  const resetAfter = options.resetMs ?? 200

  let buffer = ''
  let lastAt = 0
  let timer: ReturnType<typeof setTimeout> | null = null

  const clear = () => {
    buffer = ''
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  const isTyping = (): boolean => {
    const active = target.activeElement
    if (active === null || active === undefined) return false
    const tag = active.tagName
    return tag === 'INPUT' || tag === 'TEXTAREA' || (active as HTMLElement).isContentEditable === true
  }

  const handler = (event: Event) => {
    const key = event as KeyboardEvent
    if (isTyping()) return

    const now = Date.now()
    const gap = now - lastAt
    lastAt = now

    if (key.key === 'Enter') {
      const code = buffer
      clear()
      if (code.length >= minLength) onScan(code)
      return
    }

    // One printable character. Modifier chords are a person, not a scanner.
    if (key.key.length !== 1 || key.ctrlKey || key.metaKey || key.altKey) return

    // A slow character starts a new candidate rather than extending the old one.
    if (gap > maxGap && buffer.length > 0) buffer = ''
    buffer += key.key

    if (timer !== null) clearTimeout(timer)
    timer = setTimeout(clear, resetAfter)
  }

  target.addEventListener('keydown', handler, true)
  return () => {
    target.removeEventListener('keydown', handler, true)
    clear()
  }
}

/** Is this string plausibly a product barcode rather than a loyalty card? */
export const looksLikeBarcode = (code: string): boolean => /^\d{8,14}$/.test(code)
